import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, MapPin } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories } from "@/lib/hooks";
import { PageHeader, StatusBadge, Money, Empty, AuthImage } from "@/components/common";
import { Schedule } from "@/components/Schedule";

function LeaseCard({ l, staff, defaultOpen }) {
  const { t, fdate } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const terminate = async () => {
    if (!window.confirm(`${t("terminate")} ?`)) return;
    try { await api.post(`/leases/${l.id}/terminate`); qc.invalidateQueries(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rise overflow-hidden rounded-sm border border-slate-200 bg-white" data-testid={`lease-card-${l.id}`}>
      <div className="grid gap-0 md:grid-cols-[220px_1fr]">
        <AuthImage path={l.property?.photos?.[0] || "https://images.unsplash.com/photo-1704040686413-2c607dbd2f06?w=700&q=80"} className="h-40 w-full object-cover md:h-full" />
        <div className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="eyebrow">{label(l.unit?.type)} · <span className="font-data">{l.unit?.reference}</span></div>
              <h3 className="mt-1 font-display text-2xl font-bold">{l.property?.name}</h3>
              <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} />{l.property?.address}, {l.property?.city}</div>
            </div>
            <StatusBadge value={l.status} label={l.status === "active" ? "Actif" : "Résilié"} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("tenant")}</div><div data-testid={`lease-tenant-${l.id}`}>{l.tenant?.name}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("start")} → {t("end")}</div><div className="text-xs">{fdate(l.start_date)} → {fdate(l.end_date)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("rent")} + {t("charges")}</div><Money amount={l.rent + l.charges} currency={l.currency} /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("deposit")}</div><Money amount={l.deposit} currency={l.currency} /></div>
          </div>
          <div className="mt-3 text-xs text-slate-500">{l.furnished ? t("furnished") : "Non meublé"} · {l.duration_type === "courte" ? t("short") : t("long")} · <span className="font-data">{l.currency}</span></div>
          <div className="mt-4 flex items-center gap-4">
            <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm font-semibold text-[#996515]" data-testid={`toggle-schedule-${l.id}`}>
              {t("schedule")} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {staff && l.status === "active" && <button onClick={terminate} className="text-xs text-slate-400 hover:text-rose-600" data-testid={`terminate-lease-${l.id}`}>{t("terminate")}</button>}
          </div>
        </div>
      </div>
      {open && <div className="border-t border-slate-200 px-5 py-4"><Schedule leaseId={l.id} /></div>}
    </div>
  );
}

export default function Leases() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const { data = [], isLoading } = useQuery({ queryKey: ["leases"], queryFn: () => fetcher("/leases") });
  const tenant = user.role === "locataire";
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  return (
    <div data-testid="leases-page">
      <PageHeader eyebrow={tenant ? t("tenant") : t("schedule")} title={tenant ? t("myHome") : t("leases")} />
      {staff && <p className="-mt-4 mb-6 text-sm text-slate-500">{t("newLease")} : {t("properties")} → {t("units")} ({t("vacant").toLowerCase()}).</p>}
      {!isLoading && data.length === 0 && <Empty />}
      <div className="space-y-5">{data.map((l) => <LeaseCard key={l.id} l={l} staff={staff} defaultOpen={tenant} />)}</div>
    </div>
  );
}
